import { createSlice } from "@reduxjs/toolkit";
import { resetAll } from "./baseSlice";
import intl from "../../i18n/intl";
import { toast } from "react-toastify";
import { errorText, TOO_MANY_ATTEMPTS } from "../../helpers/ErrorMessage";

const initialState = {
  configuration: null,
  requesting: false,
  error: null,
  action: null,
  registrationSuccess: false,
  emailVerificationSuccess: false,
};

const customIntl = intl();

const registrationSlice = createSlice({
  name: "registration",
  initialState,
  extraReducers: (builder) => builder.addCase(resetAll, () => initialState),
  reducers: {
    resetState: (state, action) => {
      state.requesting = false;
      state.error = null;
      state.action = null;
      state.registrationSuccess = false;
      state.emailVerificationSuccess = false;
    },

    // Configuration actions
    configurationRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "fetch-configuration";
    },
    configurationSuccess: (state, action) => {
      state.requesting = false;
      state.error = null;
      state.action = null;
      state.configuration = action.payload;
    },
    configurationFail: (state, action) => {
      state.requesting = false;
      state.error = action.payload;
      state.action = null;
      toast.error(
        customIntl.formatMessage({
          id: "configuration_fetch_error",
        })
      );
    },

    // Registration actions
    registrationRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "register-user";
      state.registrationSuccess = false;
    },
    registrationSuccess: (state, action) => {
      state.requesting = false;
      state.error = null;
      state.action = null;
      state.registrationSuccess = true;
      toast.success(
        customIntl.formatMessage({
          id: "registration_success",
        })
      );
    },
    registrationFail: (state, action) => {
      state.requesting = false;
      state.error = action.payload;
      state.action = null;
      state.registrationSuccess = false;

      // Handle specific API error messages
      const errorResponse = action.payload?.response;

      if (errorResponse === TOO_MANY_ATTEMPTS) {
        // Over the rate limit (F21): how long to wait
        toast.error(errorText(action.payload, customIntl));
      } else if (errorResponse) {
        // Map API error codes to user-friendly messages
        const errorMessageKey = errorResponse.toLowerCase();

        // Use formatMessage with defaultMessage fallback
        const errorMessage = customIntl.formatMessage(
          { id: errorMessageKey },
          {},
          customIntl.formatMessage({ id: "registration_error" })
        );

        toast.error(errorMessage);
      } else if (action.payload?.errors) {
        // Handle specific field errors
        Object.keys(action.payload.errors).forEach((field) => {
          toast.error(`${field}: ${action.payload.errors[field].join(", ")}`);
        });
      } else {
        // Default error message
        toast.error(
          customIntl.formatMessage({
            id: "registration_error",
          })
        );
      }
    },

    // Email verification actions
    verifyEmailRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "verify-email";
      state.emailVerificationSuccess = false;
    },
    verifyEmailSuccess: (state, action) => {
      state.requesting = false;
      state.error = null;
      state.action = null;
      state.emailVerificationSuccess = true;
      toast.success(
        customIntl.formatMessage({
          id: "email_verification_success",
        })
      );
    },
    verifyEmailFail: (state, action) => {
      state.requesting = false;
      state.error = action.payload;
      state.action = null;
      state.emailVerificationSuccess = false;

      // Handle specific API error messages for email verification
      const errorResponse = action.payload?.response;

      if (errorResponse) {
        const errorMessageKey = errorResponse.toLowerCase();
        const errorMessage = customIntl.formatMessage(
          { id: errorMessageKey },
          {},
          customIntl.formatMessage({ id: "email_verification_error" })
        );
        toast.error(errorMessage);
      } else {
        toast.error(
          customIntl.formatMessage({
            id: "email_verification_error",
          })
        );
      }
    },
  },
});

export const {
  resetState,
  configurationRequest,
  configurationSuccess,
  configurationFail,
  registrationRequest,
  registrationSuccess,
  registrationFail,
  verifyEmailRequest,
  verifyEmailSuccess,
  verifyEmailFail,
} = registrationSlice.actions;

export default registrationSlice.reducer;
